import {
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  Suspense,
  cloneElement,
  forwardRef,
  isValidElement,
  lazy,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { Button, type ButtonProps } from './Button';
import type { Placement } from './Popover';
import { mergeRefs } from './refs';

const TooltipPopup = lazy(() => import('./TooltipPopup'));

export type TooltipProps = {
  content: ReactNode;
  children: ReactElement;
  placement?: Placement;
  /** Hover delay before the first tooltip of a group opens (ms). Moving to a neighbour is instant. */
  delay?: number;
  /** Platform-aware shortcut shown after the content, e.g. `mod+k`. */
  shortcut?: string;
  disabled?: boolean;
};

/** Delay group shared by every tooltip: right after one closed, the next opens without delay. */
const group = { lastClosed: -Infinity, current: null as null | (() => void) };
const GROUP_WINDOW_MS = 400;

/** Whether focus on the element is keyboard focus (`:focus-visible`); true where it cannot be told. */
function showsFocusRing(el: HTMLElement): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

const now = () => (typeof performance === 'undefined' ? 0 : performance.now());

/**
 * Hover / keyboard-focus tooltip (dark, small) on the layer stack. The child must accept a ref. The
 * popup (Base UI) loads with the first tooltip shown, so tooltips cost nothing on first paint; the
 * trigger element itself is never wrapped or replaced. Escape hides it and still reaches the layer
 * below; touch never shows tooltips.
 */
/** A pointer that "enters" within this time of the trigger's first render did not move there. */
const APPEARED_UNDER_POINTER_MS = 350;
/** Focus that returns to a trigger within this time of its menu or popover closing shows no tooltip. */
const POPUP_RETURN_MS = 800;

export function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 500,
  shortcut,
  disabled,
}: TooltipProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Focus that follows a pointer press is not keyboard focus: no tooltip for clicks.
  const pressed = useRef(false);
  // A trigger that appears under a resting pointer (an editor opening where "Edit" was clicked) is
  // "entered" by the browser at once: that is not the member pointing at it, so no tooltip pops.
  const born = useRef(now());
  const id = useId();
  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const hide = useCallback(() => {
    clear();
    setOpen((was) => {
      if (was) group.lastClosed = now();
      return false;
    });
  }, []);
  const show = (immediate: boolean) => {
    clear();
    const instant = immediate || now() - group.lastClosed < GROUP_WINDOW_MS;
    const run = () => {
      if (anchor?.getAttribute('aria-expanded') === 'true') return;
      if (group.current && group.current !== hide) group.current();
      group.current = hide;
      setOpen(true);
    };
    if (instant) run();
    else timer.current = setTimeout(run, delay);
  };
  useEffect(() => () => clear(), []);
  // A tooltip that is switched off while it is showing (a drag started from its trigger) goes away at once.
  useEffect(() => {
    if (disabled) hide();
  }, [disabled, hide]);
  // A trigger that opens a menu or popover (`aria-expanded`) hides its tooltip while that is open, and
  // gets none when focus returns to it afterwards: the member just acted there, and the tooltip would
  // sit over what the menu item opened (a side panel's close button).
  const popupClosedAt = useRef(-Infinity);
  useEffect(() => {
    if (!anchor || typeof MutationObserver === 'undefined') return;
    const watch = () => {
      if (anchor.getAttribute('aria-expanded') === 'true') {
        hide();
        popupClosedAt.current = Infinity;
      } else if (popupClosedAt.current === Infinity) popupClosedAt.current = now();
    };
    const observer = new MutationObserver(watch);
    observer.observe(anchor, { attributes: true, attributeFilter: ['aria-expanded'] });
    return () => observer.disconnect();
  }, [anchor, hide]);

  if (!isValidElement(children)) return children;
  if (!content || disabled) return children;
  const own = children.props as Record<string, unknown>;
  const chain =
    <E,>(name: string, mine: (e: E) => void) =>
    (e: E) => {
      (own[name] as ((ev: E) => void) | undefined)?.(e);
      mine(e);
    };
  const trigger = cloneElement(children, {
    ref: mergeRefs<HTMLElement>(own.ref, setAnchor),
    'aria-describedby': open
      ? [own['aria-describedby'], id].filter(Boolean).join(' ')
      : (own['aria-describedby'] as string | undefined),
    onPointerEnter: chain<PointerEvent<HTMLElement>>('onPointerEnter', (e) => {
      if (e.pointerType !== 'mouse') return;
      // The browser's own "enter" of a pointer that did not move (the trigger just appeared under it).
      const resting =
        e.nativeEvent.isTrusted &&
        e.movementX === 0 &&
        e.movementY === 0 &&
        now() - born.current < APPEARED_UNDER_POINTER_MS;
      if (!resting) show(false);
    }),
    onPointerLeave: chain<PointerEvent<HTMLElement>>('onPointerLeave', () => hide()),
    onPointerDown: chain<PointerEvent<HTMLElement>>('onPointerDown', () => {
      pressed.current = true;
      hide();
    }),
    onFocus: chain<FocusEvent<HTMLElement>>('onFocus', (e) => {
      // Keyboard focus only: a click does not pop a tooltip, and neither does focus that returns to a
      // trigger after its menu or dialog closed by pointer (it would linger over what opened next).
      if (now() - popupClosedAt.current < POPUP_RETURN_MS) return;
      if (!pressed.current && showsFocusRing(e.currentTarget)) show(true);
    }),
    onBlur: chain<FocusEvent<HTMLElement>>('onBlur', () => {
      pressed.current = false;
      hide();
    }),
    onKeyDown: chain<KeyboardEvent<HTMLElement>>('onKeyDown', (e) => {
      if (e.key === 'Escape') hide();
    }),
  } as Record<string, unknown>);
  return (
    <>
      {trigger}
      {open && anchor ? (
        <Suspense fallback={null}>
          <TooltipPopup id={id} anchor={anchor} placement={placement} content={content} shortcut={shortcut} />
        </Suspense>
      ) : null}
    </>
  );
}

export type IconButtonProps = Omit<ButtonProps, 'iconOnly' | 'children' | 'label' | 'icon'> & {
  icon: NonNullable<ButtonProps['icon']>;
  /** Accessible name and tooltip text. */
  label: string;
  /** Shortcut shown in the tooltip (`mod+z`). */
  shortcut?: string;
  tooltipPlacement?: Placement;
  /** Hide the tooltip (the label is still the accessible name). */
  noTooltip?: boolean;
};

/** Icon-only button with a styled tooltip derived from `label` (never a native `title`). */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, shortcut, tooltipPlacement, noTooltip, ...rest },
  ref,
) {
  const button = <Button ref={ref} iconOnly label={label} tooltip={false} {...rest} />;
  if (noTooltip) return button;
  return (
    <Tooltip content={label} shortcut={shortcut} placement={tooltipPlacement}>
      {button}
    </Tooltip>
  );
});
